// src/views/maison.js
import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./maison.css";
import CalendrierMaison from "./calendrierMaison";
import Icon from "../components/Icon";

// extrait un numéro depuis "img12.jpg" -> 12
const extractNumber = (path) => {
  const file = path.split("/").pop();
  const m = file.match(/\d+/);
  return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
};

const importAllSorted = (r) =>
  r
    .keys()
    .sort((a, b) => extractNumber(a) - extractNumber(b))
    .map(r);

const Maison = () => {
  const photosExterieur = importAllSorted(
    require.context(
      "../../public/img/galerie/lamaison-exterieur",
      false,
      /\.(png|jpe?g|svg)$/
    )
  );

  const photosInterieur = importAllSorted(
    require.context(
      "../../public/img/galerie/lamaison-interieur",
      false,
      /\.(png|jpe?g|svg)$/
    )
  );

  const photos = [...photosExterieur, ...photosInterieur];

  const sliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 3000,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  // ---- FIXED "après" que le calendrier ait touché le haut de l'écran
  const rightRef = useRef(null);
  const [isFixed, setIsFixed] = useState(false);

  useEffect(() => {
    const el = rightRef.current;
    if (!el) return;

    const STICKY_TOP = 120;

    const compute = () => {
      // position du bloc (dans le document) quand il est "normal"
      const initialTop = el.getBoundingClientRect().top + window.scrollY;
      return initialTop;
    };

    let initialTop = compute();

    const onScroll = () => {
      setIsFixed(window.scrollY >= initialTop - STICKY_TOP);
    };

    const onResize = () => {
      // recalcule si responsive / images changent la hauteur
      initialTop = compute();
      onScroll();
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div className="hebergement-page">
      <h1>La Maison</h1>
      <p>
        Charmante maison de caractère, idéale pour un séjour en couple ou en
        famille. Vous profiterez d’un cadre paisible et de tout le confort
        moderne au cœur du Hameau du Sorbier.
      </p>

      {/* Carousel */}
      <Slider {...sliderSettings} className="hebergement-carousel">
        {photos.map((src, i) => (
          <div key={i}>
            <img src={src} alt={`maison-${i}`} />
          </div>
        ))}
      </Slider>

      {/* Contenu principal en 2 colonnes */}
      <div className="hebergement-content">
        <div className="hebergement-left">
          {/* Couchage */}
          <section>
            <h2>Couchage</h2>
            <ul>
              <li>6 voyageurs, 3 chambres, 3 lits, 2 salles de bain</li>
              <li>
                Suite parental : lit 140 cm, salle de bain privative avec douche
                à l'Italienne
              </li>
              <li>La chambre cosy : lit 140 cm</li>
              <li>La grande chambre : lit 160 cm</li>
              <li>Salle de bain de l'étage : baignoire et buanderie</li>
            </ul>
          </section>
          <div className="section-divider"></div>

          {/* Équipements */}
          <section>
            <h2>Équipements et services</h2>
            <ul className="equipements-list">
              <h4>Confort et commodités</h4>
              <br />
              <li>
                <Icon name="i-linge" /> Linge de maison (linge de lit, torchon)
              </li>
              <li>
                <Icon name="i-dressing" /> Dressing
              </li>
              <li>
                <Icon name="i-ventilateur" /> Ventilateur portable
              </li>
              <li>
                <Icon name="i-chauffage" /> Chauffage radiant
              </li>
              <li></li>

              <br />
              <h4>Cuisine et repas</h4>
              <br />
              <li>
                <Icon name="i-plaque-cuisson" /> Plaque de cuisson
              </li>
              <li>
                <Icon name="i-four" /> Four
              </li>
              <li>
                <Icon name="i-micro-ondes" /> Micro-ondes
              </li>
              <li>
                <Icon name="i-refrigerateur" /> Réfrigérateur
              </li>
              <li>
                <Icon name="i-congelateur" /> Congélateur
              </li>
              <li>
                <Icon name="i-grille-pain" /> Grille-pain
              </li>
              <li>
                <Icon name="i-cafetiere" /> Cafetière
              </li>
              <li>
                <Icon name="i-bouilloire" /> Bouilloire
              </li>
              <li>
                <Icon name="i-blender" /> Blender
              </li>
              <li>
                <Icon name="i-lave-vaisselle" /> Lave-vaisselle
              </li>
              <li>
                <Icon name="i-vaisselle-couverts" /> Vaisselle et couverts
              </li>
              <li>
                <Icon name="i-ustensiles-cuisine" /> Ustensiles de cuisine
              </li>
              <li></li>

              <br />
              <h4>Salle de bain et buanderie</h4>
              <br />
              <li>
                <Icon name="i-douche-italienne" /> Douche à l'Italienne
              </li>
              <li>
                <Icon name="i-baignoire" /> Baignoire
              </li>
              <li>
                <Icon name="i-shampooing" /> Shampooing
              </li>
              <li>
                <Icon name="i-gel-douche" /> Gel douche
              </li>
              <li>
                <Icon name="i-serviettes-gants" /> Serviettes et gants
              </li>
              <li>
                <Icon name="i-seche-cheveux" /> Sèche-cheveux
              </li>
              <li>
                <Icon name="i-lave-linge" /> Lave-linge
              </li>
              <li>
                <Icon name="i-fer-repasser" /> Fer et planche à repasser
              </li>
              <li>
                <Icon name="i-etendoire" /> Étendoire à linge
              </li>
              <li>
                <Icon name="i-produits-nettoyage" /> Produits de nettoyage
              </li>
              <li>
                <Icon name="i-ustensiles-nettoyage" /> Ustensiles de nettoyage
                (balai, serpillière, seau, etc.)
              </li>

              <br />
              <h4>Divertissements</h4>
              <br />
              <li>
                <Icon name="i-wifi-fibre" /> Wi-Fi (Fibre optique)
              </li>
              <li>
                <Icon name="i-television" /> Télévision
              </li>
              <li>
                <Icon name="i-tennis-table" /> Tennis de table
              </li>
              <li>
                <Icon name="i-petanque" /> Terrain de pétanque
              </li>
              <li>
                <Icon name="i-molkky" /> Molkky
              </li>
              <li>
                <Icon name="i-salle-jeux" /> Salle de Jeux
              </li>
              <li>
                <Icon name="i-jeu-flechette" /> Jeu de fléchette
              </li>
              <li>
                <Icon name="i-livres" /> Livres
              </li>
              <li>
                <Icon name="i-jeux-societe" /> Jeux de société
              </li>

              <br />
              <h4>Extérieur</h4>
              <br />
              <li>
                <Icon name="i-douche-piscine" /> Douche piscine
              </li>
              <li>
                <Icon name="i-serviettes-piscine" /> Serviettes piscine
              </li>
              <li>
                <Icon name="i-transats" /> Transats
              </li>
              <li>
                <Icon name="i-parasol" /> Parasol
              </li>
              <li>
                <Icon name="i-arriere-cour" /> Arrière cour privée et cloturée
              </li>
              <li>
                <Icon name="i-espace-repas-exterieur" /> Espace repas extérieur
              </li>
              <li>
                <Icon name="i-tonnelle" /> Tonnelle
              </li>
              <li>
                <Icon name="i-plancha" /> Plancha
              </li>
              <li>
                <Icon name="i-brazero-barbecue" /> Brazero - Barbecue
              </li>
              <li>
                <Icon name="i-ustensiles-barbecue" /> Ustensiles de Barbecue
              </li>
              <li></li>

              <br />
              <h4>Autres</h4>
              <br />
              <li>
                <Icon name="i-alarme-piscine" /> Alarme piscine
              </li>
              <li>
                <Icon name="i-detecteur-fumee" /> Détecteur de fumée
              </li>
              <li>
                <Icon name="i-parking" /> Parking privatif
              </li>
              <li>
                <Icon name="i-lit-chaise-haute-bebe" /> Lit et chaise haute bébé
                (sur demande)
              </li>
            </ul>
          </section>
          <div className="section-divider"></div>

          {/* Tarifs */}
          <section>
            <h2>Tarifs</h2>
            <p>Réservation du Samedi au Samedi</p>
            <p>- Juillet : 2000€ la semaine</p>
            <p>- Août : 2500€ la semaine</p>
            <p>- À tout autre moment de l'année 1800€ la semaine</p>
          </section>
          <div className="section-divider"></div>

          {/* Infos complémentaires */}
          <section>
            <h2>Informations complémentaires</h2>
            <p>Animaux non autorisés</p>
          </section>
        </div>

        {/* Colonne droite : calendrier */}
        <div
          ref={rightRef}
          className={`hebergement-right ${isFixed ? "is-fixed" : ""}`}
        >
          <CalendrierMaison />
        </div>
      </div>
    </div>
  );
};

export default Maison;
